/**
 * Location groups admin API module — named sets of an account's locations
 * ("North District") that the settings surfaces can act on as one.
 *
 * Endpoints (all under /accounts/{accountId}/location-groups):
 *   GET    /                    → { groups: [...] } with per-group location counts
 *   POST   /                    → 201 group; 409 LOCATION_GROUP_NAME_TAKEN
 *   PUT    /{gid}               → 200 group; 404 LOCATION_GROUP_NOT_FOUND; 409 name taken
 *   DELETE /{gid}               → 204 (members are ungrouped, their settings untouched)
 *   PUT    /{gid}/locations     → 200 { group_id, location_ids } — REPLACES membership;
 *                                 a listed location moves in from any other group
 *   GET    /{gid}/locations     → { locations: [{ id, name }] }
 *
 * A location belongs to at most one group; every location listing carries
 * location_group_id / location_group_name so the UI can say where it is.
 */
import { webApiClient } from '../apiClient';
import { dedup } from './helpers';

export interface LocationGroup {
  id: string;
  account_id: string;
  name: string;
  description: string | null;
  location_count: number;
  created_at?: string;
  updated_at?: string;
}

export interface LocationGroupMember {
  id: string;
  name: string;
}

export interface LocationGroupBody {
  name?: string;
  description?: string | null;
}

export interface LocationGroupMembership {
  group_id: string;
  location_ids: string[];
}

/** Server error codes this module recognises. */
export type LocationGroupErrorCode = 'LOCATION_GROUP_NAME_TAKEN' | 'LOCATION_GROUP_NOT_FOUND';

export class LocationGroupError extends Error {
  code?: LocationGroupErrorCode | string;
  constructor(message: string, code?: string) {
    super(message);
    this.name = 'LocationGroupError';
    this.code = code;
  }
}

const MESSAGES: Record<string, string> = {
  LOCATION_GROUP_NAME_TAKEN: 'A location group with that name already exists.',
  LOCATION_GROUP_NOT_FOUND: 'That location group no longer exists.',
};

/** The ApiClient flattens the error body: the code lives on `errorBody`, not the thrown message. */
export function toLocationGroupError(res: { error?: string; errorBody?: any }, fallback: string): LocationGroupError {
  const eb: any = res.errorBody;
  const code: string | undefined = eb?.code || eb?.error_code;
  if (code && MESSAGES[code]) return new LocationGroupError(MESSAGES[code], code);
  const message = eb?.message || eb?.error || res.error || fallback;
  return new LocationGroupError(message, code);
}

const base = (accountId: string) => `/accounts/${accountId}/location-groups`;

export async function getLocationGroups(accountId: string): Promise<LocationGroup[]> {
  return dedup(`getLocationGroups:${accountId}`, async () => {
    const res = await webApiClient.get<{ groups: LocationGroup[] }>(base(accountId));
    if (!res.success) throw toLocationGroupError(res, 'Failed to load location groups');
    // nil slice → null from Go; normalise so callers can map.
    return Array.isArray(res.data?.groups) ? res.data.groups : [];
  });
}

export async function createLocationGroup(accountId: string, body: { name: string; description?: string }): Promise<LocationGroup> {
  const res = await webApiClient.post<LocationGroup>(base(accountId), body);
  if (!res.success) throw toLocationGroupError(res, 'Failed to create the location group');
  return res.data as LocationGroup;
}

export async function updateLocationGroup(accountId: string, groupId: string, body: LocationGroupBody): Promise<LocationGroup> {
  const res = await webApiClient.put<LocationGroup>(`${base(accountId)}/${groupId}`, body);
  if (!res.success) throw toLocationGroupError(res, 'Failed to update the location group');
  return res.data as LocationGroup;
}

export async function deleteLocationGroup(accountId: string, groupId: string): Promise<void> {
  const res = await webApiClient.delete(`${base(accountId)}/${groupId}`);
  if (!res.success) throw toLocationGroupError(res, 'Failed to delete the location group');
}

/** Replace the group's membership. Listed locations leave any other group; [] empties it. */
export async function setLocationGroupMembers(accountId: string, groupId: string, locationIds: string[]): Promise<LocationGroupMembership> {
  const res = await webApiClient.put<LocationGroupMembership>(`${base(accountId)}/${groupId}/locations`, { location_ids: locationIds });
  if (!res.success) throw toLocationGroupError(res, 'Failed to update the group’s locations');
  const d = res.data;
  return { group_id: d?.group_id ?? groupId, location_ids: Array.isArray(d?.location_ids) ? d.location_ids : locationIds };
}

export async function getLocationGroupMembers(accountId: string, groupId: string): Promise<LocationGroupMember[]> {
  const res = await webApiClient.get<{ locations: LocationGroupMember[] }>(`${base(accountId)}/${groupId}/locations`);
  if (!res.success) throw toLocationGroupError(res, 'Failed to load the group’s locations');
  return Array.isArray(res.data?.locations) ? res.data.locations : [];
}

export const locationGroupsAdmin = {
  getLocationGroups,
  createLocationGroup,
  updateLocationGroup,
  deleteLocationGroup,
  setLocationGroupMembers,
  getLocationGroupMembers,
};
